// src/components/AdminSubSidebarIcon.jsx
//
// Per-item icon renderer for the AdminPage's sub-sidebar.
// Mirrors the NavRailIcon.jsx pattern (verbatim from legacy monolith)
// — single file, dispatched by `itemKey`, inline 24×24 SVG paths,
// stroke="currentColor" so the colour follows the parent's `color`.
//
// Design choice (Slice Adm1, 2026-05-12): we picked SVG over emoji
// because AdminSubSidebar is structurally a sidebar nav (same role as
// NavRail), not a content-tab strip (LocationDetail's idiom). Keeping
// the navigation language consistent with NavRail is the right call.
// Outline-only — no filled variant — because the active state in the
// mockup is conveyed by background + border, not by an icon swap.
//
// ADMIN-NAV-EXPAND (2026-06-11): added 7 new icon branches for the
// expanded nav — users_sso, app_branding, ai_governance, integrations,
// notifications_slack, retention_privacy, markets_location_ids.
//
// Adding a new section? Add a new `if (itemKey === ...)` branch with a
// 24×24 outline SVG that visually matches the others (stroke 1.6,
// round caps, round joins). Keep the file scannable.

import React from "react";

export function AdminSubSidebarIcon({ itemKey, size = 18 }) {
  const s = { width: size, height: size, display: "block", flexShrink: 0 };
  const common = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.6,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };

  // currency → dollar sign in a circle
  if (itemKey === "currency") {
    return (
      <svg style={s} {...common}>
        <circle cx="12" cy="12" r="9" />
        <path d="M15 9.2c-.6-.8-1.6-1.3-2.8-1.3-1.7 0-3 .9-3 2.1 0 1.2 1.3 1.8 3 2.1 1.7.3 3 .9 3 2.1 0 1.2-1.3 2.1-3 2.1-1.5 0-2.6-.7-2.9-1.6" />
        <path d="M12 6.5v11" />
      </svg>
    );
  }

  // activity → rising usage trend
  if (itemKey === "activity") {
    return (
      <svg style={s} {...common}>
        <path d="M3 17l5-5 4 3 7-8" />
        <path d="M15 7h4v4" />
        <path d="M3 21h18" />
      </svg>
    );
  }

  // data → database cylinder (top ellipse + side walls + mid shelf)
  if (itemKey === "data") {
    return (
      <svg style={s} {...common}>
        <ellipse cx="12" cy="5" rx="7" ry="2.5" />
        <path d="M5 5v14c0 1.4 3.1 2.5 7 2.5s7-1.1 7-2.5V5" />
        <path d="M5 12c0 1.4 3.1 2.5 7 2.5s7-1.1 7-2.5" />
      </svg>
    );
  }

  // data_trust → shield with a check
  if (itemKey === "data_trust") {
    return (
      <svg style={s} {...common}>
        <path d="M12 2l8 3v6c0 4.4-3.4 8.5-8 10-4.6-1.5-8-5.6-8-10V5l8-3z" />
        <polyline points="8.5 11.5 11 14 15.5 9.5" />
      </svg>
    );
  }

  // import → arrow up out of tray
  if (itemKey === "import") {
    return (
      <svg style={s} {...common}>
        <path d="M12 3v12" />
        <polyline points="7 8 12 3 17 8" />
        <path d="M5 17v2a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-2" />
      </svg>
    );
  }

  // export → arrow down into tray
  if (itemKey === "export") {
    return (
      <svg style={s} {...common}>
        <path d="M12 15V3" />
        <polyline points="7 10 12 15 17 10" />
        <path d="M5 17v2a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-2" />
      </svg>
    );
  }

  // integrations → plug
  if (itemKey === "integrations") {
    return (
      <svg style={s} {...common}>
        <path d="M9 2v4" />
        <path d="M15 2v4" />
        <path d="M7 6h10v4a5 5 0 0 1-10 0V6z" />
        <path d="M12 15v4" />
        <path d="M9 22h6" />
      </svg>
    );
  }

  // audit → horizontal lines (a log)
  if (itemKey === "audit") {
    return (
      <svg style={s} {...common}>
        <rect x="3" y="4" width="18" height="16" rx="2" />
        <line x1="7" y1="9"  x2="17" y2="9"  />
        <line x1="7" y1="13" x2="17" y2="13" />
        <line x1="7" y1="17" x2="13" y2="17" />
      </svg>
    );
  }

  // build_status → clipboard with checklist
  if (itemKey === "build_status") {
    return (
      <svg style={s} {...common}>
        <rect x="5" y="2" width="14" height="20" rx="2" />
        <line x1="9"  y1="8"  x2="15" y2="8"  />
        <line x1="9"  y1="12" x2="15" y2="12" />
        <polyline points="9 16 11 18 15 14" />
        <path d="M9 2v2h6V2" />
      </svg>
    );
  }

  // roles → two people
  if (itemKey === "roles") {
    return (
      <svg style={s} {...common}>
        <circle cx="9"  cy="8" r="3" />
        <circle cx="17" cy="9" r="2.2" />
        <path d="M3 20c0-3 2.7-5 6-5s6 2 6 5" />
        <path d="M15 20c0-2.2 2-4 4-4s2 1 2 1" />
      </svg>
    );
  }

  // sensitivity → shield with a central keyhole (access classification)
  if (itemKey === "sensitivity") {
    return (
      <svg style={s} {...common}>
        <path d="M12 2l8 3v6c0 4.4-3.4 8.5-8 10-4.6-1.5-8-5.6-8-10V5l8-3z" />
        <circle cx="12" cy="10.5" r="1.8" />
        <path d="M12 12.3v3.2" />
      </svg>
    );
  }

  // nav_features → two horizontal tracks with circle handles (sliders/controls)
  if (itemKey === "nav_features") {
    return (
      <svg style={s} {...common}>
        <line x1="4"  y1="8"  x2="9"  y2="8"  />
        <line x1="17" y1="8"  x2="20" y2="8"  />
        <line x1="4"  y1="16" x2="7"  y2="16" />
        <line x1="11" y1="16" x2="20" y2="16" />
        <circle cx="13" cy="8"  r="2" />
        <circle cx="9"  cy="16" r="2" />
      </svg>
    );
  }

  // radar_signal_noise → radar rings with a sweep and signal point
  if (itemKey === "radar_signal_noise") {
    return (
      <svg style={s} {...common}>
        <circle cx="12" cy="12" r="8" />
        <path d="M12 12l5.7-5.7" />
        <path d="M4.8 16a8 8 0 0 0 11.2 3" />
        <circle cx="17.7" cy="6.3" r="1.4" fill="currentColor" stroke="none" />
      </svg>
    );
  }

  // amenities → grid of four squares (checklist feel)
  if (itemKey === "amenities") {
    return (
      <svg style={s} {...common}>
        <rect x="3"  y="3"  width="7" height="7" rx="1" />
        <rect x="14" y="3"  width="7" height="7" rx="1" />
        <rect x="3"  y="14" width="7" height="7" rx="1" />
        <rect x="14" y="14" width="7" height="7" rx="1" />
      </svg>
    );
  }

  // users_sso → person with a key badge (user + authentication)
  if (itemKey === "users_sso") {
    return (
      <svg style={s} {...common}>
        <circle cx="10" cy="8" r="3.5" />
        <path d="M3 20c0-3.3 3.1-6 7-6" />
        <circle cx="18" cy="16" r="2.5" />
        <path d="M18 18.5v2" />
        <path d="M15.5 16h-1" />
        <path d="M20.5 14.5l.7-.7" />
      </svg>
    );
  }

  // app_branding → paint palette / brush
  if (itemKey === "app_branding") {
    return (
      <svg style={s} {...common}>
        <path d="M12 2C6.5 2 2 6.5 2 12c0 4.4 2.8 8.2 6.8 9.6.5.1.7-.2.7-.5v-1.7c-2.7.6-3.3-1.3-3.3-1.3-.5-1.1-1.1-1.4-1.1-1.4-.9-.6.1-.6.1-.6 1 .1 1.5 1 1.5 1 .9 1.5 2.3 1.1 2.9.8.1-.6.3-1.1.6-1.3-2.2-.3-4.5-1.1-4.5-4.9 0-1.1.4-2 1-2.7-.1-.3-.4-1.3.1-2.7 0 0 .8-.3 2.7 1a9.4 9.4 0 0 1 5 0c1.9-1.3 2.7-1 2.7-1 .5 1.4.2 2.4.1 2.7.7.7 1 1.6 1 2.7 0 3.8-2.3 4.6-4.5 4.9.4.3.7 1 .7 2v3c0 .3.2.6.7.5A10 10 0 0 0 22 12c0-5.5-4.5-10-10-10z" />
      </svg>
    );
  }

  // ai_governance → brain / sparkle (AI + policy)
  if (itemKey === "ai_governance") {
    return (
      <svg style={s} {...common}>
        <path d="M12 2a5 5 0 0 1 5 5c0 1-.3 2-.8 2.8A4 4 0 0 1 20 13a4 4 0 0 1-4 4h-1v3h-6v-3H8a4 4 0 0 1-4-4 4 4 0 0 1 3.8-4A5 5 0 0 1 7 7a5 5 0 0 1 5-5z" />
        <line x1="12" y1="13" x2="12" y2="17" />
        <line x1="10" y1="15" x2="14" y2="15" />
      </svg>
    );
  }

  // Legacy connectors key → two nodes joined by a line. Persisted selections
  // are migrated to integrations by AdminPage, but keeping this icon branch
  // makes old section data render safely during the transition.
  if (itemKey === "connectors") {
    return (
      <svg style={s} {...common}>
        <rect x="2"  y="9" width="6" height="6" rx="1.5" />
        <rect x="16" y="9" width="6" height="6" rx="1.5" />
        <path d="M8 12h8" />
        <circle cx="12" cy="12" r="1.2" fill="currentColor" stroke="none" />
      </svg>
    );
  }

  // notifications_slack → speech bubble with a bell
  if (itemKey === "notifications_slack") {
    return (
      <svg style={s} {...common}>
        <path d="M18 8a6 6 0 0 0-12 0c0 4-2 5-2 5h16s-2-1-2-5" />
        <path d="M13.7 21a2 2 0 0 1-3.4 0" />
      </svg>
    );
  }

  // retention_privacy → shield with a lock icon
  if (itemKey === "retention_privacy") {
    return (
      <svg style={s} {...common}>
        <path d="M12 2l8 3v6c0 4.4-3.4 8.5-8 10-4.6-1.5-8-5.6-8-10V5l8-3z" />
        <rect x="9" y="11" width="6" height="5" rx="1" />
        <path d="M10 11V9a2 2 0 0 1 4 0v2" />
      </svg>
    );
  }

  // markets_location_ids → map pin with a small grid
  if (itemKey === "markets_location_ids") {
    return (
      <svg style={s} {...common}>
        <path d="M12 2a7 7 0 0 1 7 7c0 5-7 13-7 13S5 14 5 9a7 7 0 0 1 7-7z" />
        <circle cx="12" cy="9" r="2.5" />
      </svg>
    );
  }

  // space_fit → gauge / speedometer arc (fit scoring)
  if (itemKey === "space_fit") {
    return (
      <svg style={s} {...common}>
        <path d="M5 16a8 8 0 1 1 14 0" />
        <line x1="12" y1="16" x2="12" y2="10" />
        <circle cx="12" cy="16" r="1.5" fill="currentColor" stroke="none" />
        <line x1="12" y1="16" x2="16.5" y2="12" strokeWidth={1.8} />
      </svg>
    );
  }

  // benchmarks → horizontal stacked bars of different widths (banded threshold)
  if (itemKey === "benchmarks") {
    return (
      <svg style={s} {...common}>
        <rect x="3"  y="4"  width="18" height="4"  rx="1" />
        <rect x="3"  y="10" width="12" height="4"  rx="1" />
        <rect x="3"  y="16" width="16" height="4"  rx="1" />
      </svg>
    );
  }

  // non_working_days → calendar page with a marked day
  if (itemKey === "non_working_days") {
    return (
      <svg style={s} {...common}>
        <rect x="3" y="4.5" width="18" height="17" rx="2" />
        <path d="M8 2.5v4M16 2.5v4M3 9h18" />
        <circle cx="12" cy="14" r="2" />
      </svg>
    );
  }

  // space_policy → ruler (rectangle body + tick marks along top edge)
  if (itemKey === "space_policy") {
    return (
      <svg style={s} {...common}>
        <rect x="2" y="9" width="20" height="6" rx="1" />
        <line x1="6"  y1="9" x2="6"  y2="12" />
        <line x1="10" y1="9" x2="10" y2="13" />
        <line x1="14" y1="9" x2="14" y2="12" />
        <line x1="18" y1="9" x2="18" y2="12" />
      </svg>
    );
  }

  // insight_imagery → rectangle frame with mountain/sun (Image Library)
  if (itemKey === "insight_imagery") {
    return (
      <svg style={s} {...common}>
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <polyline points="21 15 16 10 5 21" />
      </svg>
    );
  }

  // recently_deleted → trash can (lid + body + two internal lines)
  if (itemKey === "recently_deleted") {
    return (
      <svg style={s} {...common}>
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
        <path d="M10 11v6" />
        <path d="M14 11v6" />
        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
      </svg>
    );
  }

  return null;
}
